import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight, Check } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { postEnquiry } from "@/lib/api";
import { PageHero, Section } from "@/components/Section";

const Contact = () => {
  const { site } = useSite();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    try {
      await postEnquiry(form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      setErr("Could not send. Please try again or call us directly.");
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Get in touch"
        title="Let's talk tools"
        intro="Whether you're a workshop, a dealer, an exporter or an OEM buyer — drop us a line. We typically reply within one business day."
        breadcrumbs={<>Home / <span className="text-white">Contact</span></>}
      />
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Info */}
          <div className="lg:col-span-5">
            <div className="space-y-6">
              <div className="border-l-2 border-[var(--jt-primary)] pl-4">
                <div className="eyebrow">Corporate Office</div>
                <div className="mt-2 font-display font-bold text-lg">{site?.address}</div>
              </div>
              <div className="border-l-2 border-[var(--jt-primary)] pl-4">
                <div className="eyebrow">Phone</div>
                <a href={`tel:${site?.phone}`} className="mt-2 block font-mono text-lg hover:text-[var(--jt-primary)]" data-testid="contact-phone">{site?.phone}</a>
              </div>
              <div className="border-l-2 border-[var(--jt-primary)] pl-4">
                <div className="eyebrow">Email</div>
                <a href={`mailto:${site?.email}`} className="mt-2 block font-mono text-lg hover:text-[var(--jt-primary)]" data-testid="contact-email">{site?.email}</a>
              </div>
            </div>

            <div className="mt-10">
              <iframe
                title="Office Location"
                width="100%"
                height="300"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src="https://maps.google.com/maps?q=A-147+Sector+83+Noida+Uttar+Pradesh&t=&z=14&ie=UTF8&iwloc=&output=embed"
              />
            </div>
          </div>

          {/* Form */}
          <form onSubmit={onSubmit} className="lg:col-span-7 bg-[var(--jt-ink)] text-white p-8 lg:p-10" data-testid="contact-form">
            <div className="eyebrow">Send a Message</div>
            <h2 className="font-display font-black uppercase text-3xl mt-2">Tell us what you need.</h2>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input required placeholder="Name *" data-testid="contact-name" className="bg-white/5 border border-white/15 px-3 py-3 text-sm font-mono focus:outline-none focus:border-[var(--jt-primary)]" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input required type="email" placeholder="Email *" data-testid="contact-email-input" className="bg-white/5 border border-white/15 px-3 py-3 text-sm font-mono focus:outline-none focus:border-[var(--jt-primary)]" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input required placeholder="Phone *" data-testid="contact-phone-input" className="bg-white/5 border border-white/15 px-3 py-3 text-sm font-mono focus:outline-none focus:border-[var(--jt-primary)]" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <input placeholder="Subject" data-testid="contact-subject" className="bg-white/5 border border-white/15 px-3 py-3 text-sm font-mono focus:outline-none focus:border-[var(--jt-primary)]" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
              <textarea required rows={6} placeholder="Your message *" data-testid="contact-message" className="bg-white/5 border border-white/15 px-3 py-3 text-sm font-mono focus:outline-none focus:border-[var(--jt-primary)] sm:col-span-2" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>

            <button type="submit" data-testid="contact-submit" className="mt-6 jt-btn jt-btn-primary">{sent ? <>Sent <Check className="w-4 h-4" /></> : <>Send Message <ArrowRight className="w-4 h-4" /></>}</button>
            {err && <div className="mt-3 font-mono text-xs text-red-400">{err}</div>}
            {sent && <div className="mt-3 font-mono text-xs text-[var(--jt-primary)]" data-testid="contact-success">Message received. We'll be in touch shortly.</div>}
          </form>
        </div>
      </Section>
    </>
  );
};

export default Contact;
